import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import BubblePopGame from "./BubblePopGame";

// A cute shark mascot that peeks up from the bottom-right corner, blinks,
// wiggles, and pops a friendly speech bubble that changes with the section
// the visitor is currently viewing.
const MESSAGES = [
  { at: 0, text: "Hi! I'm Finn. Psst... click me 3 times to play a bubble game!" },
  { id: "about", text: "This is the crew that makes the magic happen!" },
  { id: "works", text: "Ooh, check out the games we've been cooking up!" },
  { id: "blog", text: "Fresh dev diaries straight from the deep." },
  { id: "contact", text: "Say hi, we don't bite... much. \ud83e\udd88" },
];

export default function SharkMascot() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [message, setMessage] = useState(MESSAGES[0].text);
  const [showBubble, setShowBubble] = useState(false);
  const [excited, setExcited] = useState(false);
  const [gameActive, setGameActive] = useState(false);
  const clickCount = useRef(0);
  const clickTimer = useRef(null);
  const lastY = useRef(0);
  const currentSection = useRef("");

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(true);
      setShowBubble(true);
      setTimeout(() => setShowBubble(false), 7000);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const sections = ["about", "works", "blog", "contact"];
    const onScroll = () => {
      const y = window.scrollY;
      // brief excited wiggle on fast scroll
      if (Math.abs(y - lastY.current) > 40) {
        setExcited(true);
        clearTimeout(onScroll._t);
        onScroll._t = setTimeout(() => setExcited(false), 400);
      }
      lastY.current = y;

      // find the section in view
      let active = "";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.5 && rect.bottom >= window.innerHeight * 0.3) {
          active = id;
        }
      }
      if (active && active !== currentSection.current) {
        currentSection.current = active;
        const found = MESSAGES.find((m) => m.id === active);
        if (found) {
          setMessage(found.text);
          setShowBubble(true);
          clearTimeout(onScroll._bt);
          onScroll._bt = setTimeout(() => setShowBubble(false), 4000);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wave = () => {
    setExcited(true);
    setTimeout(() => setExcited(false), 600);

    // count rapid clicks to launch the bubble-pop mini-game
    clickCount.current += 1;
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => (clickCount.current = 0), 1200);

    if (clickCount.current >= 3 && !gameActive) {
      clickCount.current = 0;
      setMessage("Bubble party! Pop as many as you can! \ud83e\udee7");
      setShowBubble(true);
      setGameActive(true);
      setTimeout(() => setShowBubble(false), 3000);
      return;
    }

    const boops = [
      "Yay! Thanks for the boop. \ud83e\udee7",
      "Hehe, that tickles!",
      "One more and it's a party... \ud83d\udc40",
    ];
    setMessage(boops[Math.min(clickCount.current - 1, boops.length - 1)]);
    setShowBubble(true);
    setTimeout(() => setShowBubble(false), 2500);
  };

  if (dismissed) return null;

  return (
    <>
      <BubblePopGame active={gameActive} onClose={() => setGameActive(false)} />
      <div
        className={`fixed bottom-0 right-4 sm:right-8 z-[70] flex flex-col items-end transition-all duration-700 ease-out ${
          visible ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* Speech bubble */}
        <div
          className={`mb-3 mr-2 max-w-[230px] origin-bottom-right transition-all duration-300 ${
            showBubble ? "opacity-100 scale-100" : "opacity-0 scale-90 pointer-events-none"
          }`}
        >
          <div className="relative bg-white dark:bg-slate-800 text-slate-800 dark:text-white text-sm font-semibold rounded-2xl px-4 py-3 shadow-[0_8px_30px_rgba(14,127,168,0.18)] border-2 border-teal-300 dark:border-cyan-500/40">
            {message}
            <span className="absolute -bottom-2 right-8 w-4 h-4 bg-white dark:bg-slate-800 border-b-2 border-r-2 border-teal-300 dark:border-cyan-500/40 rotate-45" />
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={() => setDismissed(true)}
          className="self-end mr-2 mb-1 w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 flex items-center justify-center hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
          aria-label="Hide mascot"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {/* Shark */}
        <button
          onClick={wave}
          className={`relative outline-none ${excited ? "mascot-excited" : "mascot-idle"}`}
          aria-label="Boop the shark"
        >
          <FinnShark />
        </button>
      </div>
    </>
  );
}

function FinnShark() {
  return (
    <svg width="112" height="96" viewBox="0 0 112 96" className="drop-shadow-xl">
      {/* body */}
      <g>
        <ellipse cx="56" cy="70" rx="42" ry="40" fill="#3bbcd0" />
        <ellipse cx="56" cy="70" rx="42" ry="40" fill="url(#finnGrad)" />
        {/* belly */}
        <ellipse cx="56" cy="82" rx="30" ry="26" fill="#eaf7f9" />
        {/* dorsal fin */}
        <path d="M56 30 C60 12 74 8 82 6 C78 16 74 26 70 34 Z" fill="#1f97ad" />
        {/* side fins */}
        <path d="M20 70 C8 74 4 84 6 92 C16 88 26 82 30 76 Z" fill="#2aa7bd" />
        <path d="M92 70 C104 74 108 84 106 92 C96 88 86 82 82 76 Z" fill="#2aa7bd" />
        {/* eyes */}
        <circle cx="44" cy="62" r="9" fill="#fff" />
        <circle cx="68" cy="62" r="9" fill="#fff" />
        <circle cx="46" cy="63" r="4.2" fill="#0f2c38" className="mascot-eye" />
        <circle cx="70" cy="63" r="4.2" fill="#0f2c38" className="mascot-eye" />
        <circle cx="47.4" cy="61.4" r="1.4" fill="#fff" />
        <circle cx="71.4" cy="61.4" r="1.4" fill="#fff" />
        {/* blush */}
        <circle cx="34" cy="72" r="5" fill="#ff9db1" opacity="0.7" />
        <circle cx="78" cy="72" r="5" fill="#ff9db1" opacity="0.7" />
        {/* smile */}
        <path d="M48 78 Q56 86 64 78" stroke="#0f2c38" strokeWidth="2.6" fill="none" strokeLinecap="round" />
        {/* little tooth */}
        <path d="M52 80 l2 3 l2 -3 Z" fill="#fff" />
      </g>
      <defs>
        <linearGradient id="finnGrad" x1="0" y1="30" x2="0" y2="110" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4fc9dc" />
          <stop offset="1" stopColor="#2593ab" />
        </linearGradient>
      </defs>
    </svg>
  );
}
